// Shared catalog of drop-in page-builder components.
//
// Consumed by:
//  - component-library.vue  → renders the config modal when a placed component is edited
//  - chat.vue               → lists components in the composer "attach" picker
//
// Keep this the single source of truth for component types, labels and config fields.

// showWhen / showWhenNot take a 'key:value' string or an array of them; an array matches if any entry does.
const matchesCondition = (condition, values) => [].concat(condition).some((entry) => {
    const [key, value] = entry.split(':');
    return values[key] === value;
});

export const isFieldVisible = (field, values) => {
    if (field.showWhen) {
        return matchesCondition(field.showWhen, values);
    }

    if (field.showWhenNot) {
        return !matchesCondition(field.showWhenNot, values);
    }

    return true;
};

export const componentCatalog = [
    {
        type: 'lead-capture',
        nameKey: 'page:aiPageBuilder.components.leadCapture.name',
        descriptionKey: 'page:aiPageBuilder.components.leadCapture.description',
        configFields: [
            { key: 'text', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.text', type: 'text', default: '' },
            { key: 'description', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.description', type: 'text', default: '' },
            { key: 'notifications_enabled', labelKey: 'pagebuilderBlock:leadCapture.input.form.notificationsEnabled.label', type: 'boolean', default: false },
            { key: 'button_text', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.buttonText', type: 'text', default: 'Get Started' },
            { key: 'collect_title', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.collectTitle', type: 'boolean', default: false },
            { key: 'collect_phone', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.collectPhone', type: 'boolean', default: false },
            { key: 'collect_linkedin', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.collectLinkedin', type: 'boolean', default: false },
            { key: 'collect_company', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.collectCompany', type: 'boolean', default: true },
            { key: 'collect_org_nr', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.collectOrgNr', type: 'boolean', default: false },
            { key: 'collect_website', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.collectWebsite', type: 'boolean', default: false },
            { key: 'leadTag', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.leadTag', type: 'tag-select', default: '' },
            { key: 'policy', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.policy', type: 'text', default: '' },
            { key: 'success_message', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.successMessage', type: 'text', default: 'Your details have been saved' },
            { key: 'redirect', labelKey: 'page:aiPageBuilder.components.leadCapture.fields.redirect', type: 'text', default: '' },
        ],
    },
    {
        type: 'login-form',
        nameKey: 'page:aiPageBuilder.components.loginForm.name',
        descriptionKey: 'page:aiPageBuilder.components.loginForm.description',
        configFields: [],
    },
    {
        type: 'calendly',
        nameKey: 'page:aiPageBuilder.components.calendly.name',
        descriptionKey: 'page:aiPageBuilder.components.calendly.description',
        configFields: [
            { key: 'text', labelKey: 'page:aiPageBuilder.components.calendly.fields.url', type: 'text', default: '' },
        ],
    },
    {
        type: 'cronofy',
        nameKey: 'page:aiPageBuilder.components.cronofy.name',
        descriptionKey: 'page:aiPageBuilder.components.cronofy.description',
        configFields: [
            { key: 'bookableUserUuid', labelKey: 'page:aiPageBuilder.components.cronofy.fields.bookableUserUuid', type: 'bookable-user-select', default: null, required: true },
            { key: 'meetingLength', labelKey: 'page:aiPageBuilder.components.cronofy.fields.meetingLength', type: 'number', default: 30 },
            { key: 'bookingEventTitle', labelKey: 'page:aiPageBuilder.components.cronofy.fields.bookingEventTitle', type: 'text', default: '' },
        ],
    },
    {
        type: 'form',
        nameKey: 'page:aiPageBuilder.components.form.name',
        descriptionKey: 'page:aiPageBuilder.components.form.description',
        configFields: [
            { key: 'form_slug', labelKey: 'page:aiPageBuilder.components.form.fields.formSlug', type: 'form-picker', required: true, default: null },
        ],
    },
    {
        type: 'button-unified',
        nameKey: 'page:aiPageBuilder.components.button.name',
        descriptionKey: 'page:aiPageBuilder.components.button.description',
        defaultConfig: {
            padding: {
                top: 'small',
                right: 'medium',
                bottom: 'small',
                left: 'medium',
            },
        },
        configFields: [
            { key: 'label', labelKey: 'page:aiPageBuilder.components.button.fields.label', type: 'text', default: 'Click Here' },
            { key: 'button_style', labelKey: 'page:aiPageBuilder.components.button.fields.style', type: 'select', options: ['solid', 'outline', 'floating'], default: 'solid' },
            { key: 'color_mode', labelKey: 'page:aiPageBuilder.components.button.fields.colorMode', type: 'select', options: ['siteAppearance', 'custom'], default: 'siteAppearance' },
            { key: 'cssBackgroundColor', labelKey: 'page:aiPageBuilder.components.button.fields.bgColor', type: 'color', default: '#3b82f6', showWhen: 'color_mode:custom' },
            { key: 'cssColor', labelKey: 'page:aiPageBuilder.components.button.fields.textColor', type: 'color', default: '#ffffff', showWhen: 'color_mode:custom' },
            { key: 'rounding', labelKey: 'page:aiPageBuilder.components.button.fields.rounding', type: 'select', options: ['siteAppearance', 'none', 'slight', 'medium', 'very'], default: 'siteAppearance' },
            { key: 'padding_size', labelKey: 'pagebuilder:styleEditor.padding', type: 'select', options: ['small', 'medium', 'large'], default: 'medium' },
            { key: 'alignment', labelKey: 'page:aiPageBuilder.components.button.fields.alignment', type: 'select', options: ['left', 'center', 'right', 'fullwidth'], default: 'center' },
            { key: 'target_type', labelKey: 'page:aiPageBuilder.components.button.fields.targetType', type: 'select', options: ['url', 'email', 'page', 'file'], default: 'url' },
            // target_data changes shape with target_type: a string for url/email, { slug, title } for page and a media bank entry id for file.
            { key: 'target_data', labelKey: 'pagebuilderBlock:unifiedButton.input.buttonLink.label', type: 'text', default: '#', showWhen: 'target_type:url' },
            { key: 'target_data', labelKey: 'pagebuilderBlock:unifiedButton.input.buttonEmail.label', type: 'text', showWhen: 'target_type:email' },
            { key: 'target_data', labelKey: 'pagebuilderBlock:unifiedButton.input.buttonTargetPage.label', type: 'page-search', required: true, showWhen: 'target_type:page' },
            { key: 'target_data', labelKey: 'pagebuilderBlock:unifiedButton.input.buttonFile.label', type: 'media-bank-entry', mediaType: 'file', required: true, showWhen: 'target_type:file' },
            { key: 'target_open_in_new_tab', labelKey: 'pagebuilderBlock:unifiedButton.input.buttonLinkTarget.openInNewPage.label', type: 'boolean', default: false, showWhenNot: ['target_type:email', 'target_type:file'] },
        ],
    },
    {
        type: 'image-unified',
        nameKey: 'page:aiPageBuilder.components.image.name',
        descriptionKey: 'page:aiPageBuilder.components.image.description',
        defaultConfig: {
            media_width: null,
            media_height: null,
            media_width_aspect: 1,
            media_height_aspect: 1,
        },
        configFields: [
            { key: 'source_type', labelKey: 'pagebuilderBlock:general.input.source.label', type: 'select', options: ['mediaLibrary', 'pexels', 'url'], default: 'mediaLibrary' },
            { key: 'media_bank_entry_id', labelKey: 'mediaBank:selectAnImage', type: 'media-bank-gallery', mediaType: 'image', required: true, default: null, showWhen: 'source_type:mediaLibrary' },
            { key: 'media_url', labelKey: 'pagebuilderBlock:imageFromUrl.input.url.label', type: 'pexels-search', required: true, default: null, showWhen: 'source_type:pexels' },
            { key: 'media_url', labelKey: 'pagebuilderBlock:imageFromUrl.input.url.label', type: 'text', required: true, default: null, showWhen: 'source_type:url' },
            { key: 'caption', labelKey: 'pagebuilderBlock:general.input.imageCaption.label', type: 'text', default: null },
            { key: 'alt_text', labelKey: 'pagebuilderBlock:general.input.altText.label', type: 'text', default: null },
            { key: 'link_url', labelKey: 'pagebuilderBlock:general.input.link.label', type: 'text', default: null },
            { key: 'alignment', labelKey: 'pagebuilderBlock:general.input.alignment.label', type: 'select', options: ['left', 'center', 'right', 'fullwidth'], default: 'left' },
            { key: 'max_width', labelKey: 'pagebuilderBlock:general.input.imageMaxWidth.label', type: 'number', default: null, showWhenNot: 'alignment:fullwidth' },
            { key: 'max_width_unit', labelKey: 'invoiceLink:product.unit.label', type: 'select', options: ['%', 'px'], default: '%', showWhenNot: 'alignment:fullwidth' },
            { key: 'rounding', labelKey: 'pagebuilderBlock:general.input.rounding.label', type: 'select', options: ['siteAppearance', 'none', 'slight', 'medium', 'very'], default: 'siteAppearance' },
        ],
    },
    {
        type: 'price-calculator',
        nameKey: 'page:aiPageBuilder.components.priceCalculator.name',
        descriptionKey: 'page:aiPageBuilder.components.priceCalculator.description',
        configFields: [
            { key: 'heading', labelKey: 'page:aiPageBuilder.components.priceCalculator.fields.heading', type: 'text', default: 'Estimate your monthly cost' },
            { key: 'description', labelKey: 'page:aiPageBuilder.components.priceCalculator.fields.description', type: 'text', default: 'Adjust the quantity to see the total update instantly.' },
            { key: 'summary_label', labelKey: 'page:aiPageBuilder.components.priceCalculator.fields.summaryLabel', type: 'text', default: 'Estimated total' },
            { key: 'currency_symbol', labelKey: 'page:aiPageBuilder.components.priceCalculator.fields.currencySymbol', type: 'text', default: '$' },
            { key: 'base_price', labelKey: 'page:aiPageBuilder.components.priceCalculator.fields.basePrice', type: 'number', default: 120 },
            { key: 'quantity_label', labelKey: 'page:aiPageBuilder.components.priceCalculator.fields.quantityLabel', type: 'text', default: 'Seats' },
            { key: 'unit_label', labelKey: 'page:aiPageBuilder.components.priceCalculator.fields.unitLabel', type: 'text', default: 'per seat' },
            { key: 'quantity_min', labelKey: 'page:aiPageBuilder.components.priceCalculator.fields.min', type: 'number', default: 1 },
            { key: 'quantity_max', labelKey: 'page:aiPageBuilder.components.priceCalculator.fields.max', type: 'number', default: 25 },
            { key: 'quantity_step', labelKey: 'page:aiPageBuilder.components.priceCalculator.fields.step', type: 'number', default: 1 },
            { key: 'note', labelKey: 'page:aiPageBuilder.components.priceCalculator.fields.note', type: 'text', default: '' },
        ],
    },
    {
        type: 'invoice-link',
        nameKey: 'page:aiPageBuilder.components.invoiceLink.name',
        descriptionKey: 'page:aiPageBuilder.components.invoiceLink.description',
        configFields: [
            { key: 'paymentLinkUuid', labelKey: 'pagebuilderBlock:invoiceLink.linkToDisplay', type: 'invoice-link-select', required: true, default: null },
            { key: 'showProducts', labelKey: 'pagebuilderBlock:invoiceLink.showIncludedProducts', type: 'boolean', default: false },
            { key: 'purchaseButtonText', labelKey: 'pagebuilderBlock:invoiceLink.textOnPurchaseButton', type: 'text', defaultKey: 'pagebuilderBlock:invoiceLink.purchaseButton.defaultLabel', default: 'Buy now' },
            { key: 'vatDisplayMode', labelKey: 'pagebuilderBlock:invoiceLink.vatDisplayMode', type: 'select', options: ['exclVat', 'inclVat'], default: 'exclVat' },
        ],
    },
    {
        type: 'ai-content-video',
        nameKey: 'page:aiPageBuilder.components.video.name',
        descriptionKey: 'page:aiPageBuilder.components.video.description',
        configFields: [
            { key: 'media_bank_entry_id', labelKey: 'page:aiPageBuilder.components.video.fields.mediaBankEntryId', type: 'media-bank-gallery', mediaType: 'video', required: true, default: null },
            { key: 'thumbnail_media_bank_entry_id', labelKey: 'page:aiPageBuilder.components.video.fields.thumbnailMediaBankEntryId', type: 'media-bank-gallery', mediaType: 'image', required: false, default: null },
            { key: 'autoplay', labelKey: 'page:aiPageBuilder.components.video.fields.autoplay', type: 'boolean', default: false },
            { key: 'hideControls', labelKey: 'page:aiPageBuilder.components.video.fields.hideControls', type: 'boolean', default: false },
            { key: 'loop', labelKey: 'page:aiPageBuilder.components.video.fields.loop', type: 'boolean', default: false },
        ],
    },
];
